/* Sdílená pravidla pro pozvánky do týmu — čistý JS bez Reactu, aby šla logika
 * testovat bez DOM a používat současně v Nastavení i na /manazer.
 *
 * PROČ tenhle soubor vůbec je:
 *  1) Backend NIKDY nepřepíná pending → expired (tenant.py: žádný job, žádný TTL
 *     index, konstanta INV_EXPIRED se nepoužívá). Mrtvá pozvánka proto navždy
 *     vypadá jako „čekající“ — vypršení musí dopočítat UI z expires_at.
 *  2) GET /invitations vrací VŠECHNY stavy, ne jen čekající. Bez zatřídění by se
 *     pod nadpisem „Čekající pozvánky“ objevily i přijaté a zrušené.
 *  3) Hlášky z backendu jsou psané pro manažera („Poradce už má manažera“),
 *     ale poradci se ukazují v první osobě — proto se překládají tady na jednom
 *     místě, ne v každé komponentě zvlášť.
 */

/* Překlad UI textů do pěti jazyků; `lang` sem chodí parametrem,
   takže se používá varianta s výslovným jazykem (lib/prekladyUI.js). */
import { TLj } from "@/lib/prekladyUI";

/* Čas z Monga bez označení zóny: "2026-08-23T10:03:45.140000" (i s mezerou
 * místo T). Datum bez času sem schválně NEPATŘÍ — "2026-08-23" prohlížeč podle
 * specifikace čte jako UTC už sám. */
const CAS_BEZ_ZONY = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/** Datum z API (ISO string) → Date, nebo null. Nikdy nevrací Invalid Date.
 *
 * PROČ se dolepuje Z: backend ukládá i posílá UTC, ale FastAPI serializuje
 * naivní datum z Monga BEZ zóny. `new Date("2026-08-23T10:03:45")` takový tvar
 * čte jako LOKÁLNÍ čas — v létě o 2 h vedle. Dvě konkrétní lži na obrazovce:
 * pozvánka se poslední dvě hodiny platnosti tvářila jako „Vypršela" (a „Pozvat
 * znovu" nejdřív mazalo ještě PLATNOU pozvánku, takže poradce dostal 409), a
 * cokoli vzniklo mezi 22:00 a 24:00 UTC se zobrazilo o den dřív.
 * Backend teď posílá +00:00, tohle je pojistka pro starší nasazení i pro
 * ostatní routy, které přes normalizaci ještě neprošly.
 */
export function parseDatum(hodnota) {
  if (!hodnota) return null;
  let vstup = hodnota;
  if (typeof vstup === "string") {
    vstup = vstup.trim();
    if (CAS_BEZ_ZONY.test(vstup)) vstup = `${vstup.replace(" ", "T")}Z`;
  }
  const d = new Date(vstup);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Datum pro člověka, nebo null = UI musí napsat, že datum nezná (ne Invalid Date). */
export function formatDatum(hodnota, locale = "cs-CZ") {
  const d = parseDatum(hodnota);
  return d ? d.toLocaleDateString(locale) : null;
}

/** Kolik dní zbývá do vypršení. null = neznámé (chybí nebo nečitelné expires_at). */
export function zbyvaDni(expiresAt, ted = new Date()) {
  const d = parseDatum(expiresAt);
  if (!d) return null;
  return Math.ceil((d.getTime() - ted.getTime()) / 86400000);
}

/* Barvy stavů — semafor, ať je stav vidět na první pohled. Jen tokeny motivu
   (14. 10. 2026): text v *-text barvě (≥ 4,5 : 1 ve všech motivech), podklad
   jako 12–14 % plochy stavu. Hexy dřív v tmavém motivu svítily jako cizí prvek. */
const CEKA = { barva: "var(--warn-text)", bg: "color-mix(in srgb, var(--warn) 14%, transparent)" };
const DOBRE = { barva: "var(--positive-text)", bg: "color-mix(in srgb, var(--positive) 12%, transparent)" };
const SPATNE = { barva: "var(--negative-text)", bg: "color-mix(in srgb, var(--negative) 12%, transparent)" };
const NEUTRAL = { barva: "var(--muted)", bg: "color-mix(in srgb, var(--ink) 6%, transparent)" };

/**
 * Stav ODESLANÉ pozvánky očima manažera.
 * Vrací { kod, label, popis, barva, bg, lzeZrusit, lzePozvatZnovu }.
 *
 * lzePozvatZnovu u vypršelé pozvánky NENÍ kosmetika: pouhé opětovné odeslání
 * emailu backend zahodí — partial unique index (inviter+invitee where pending)
 * způsobí, že create_invitation vrátí TU SAMOU vypršelou pozvánku. Novou lze
 * vytvořit až po zrušení té staré.
 */
export function stavPozvanky(inv, ted = new Date()) {
  const status = (inv && inv.status) || "";
  if (status === "pending") {
    const dni = zbyvaDni(inv.expires_at, ted);
    if (dni !== null && dni <= 0) {
      return {
        kod: "vyprsela", label: "Vypršela",
        popis: "Poradce ji nestihl přijmout. Zruš ji a pozvi znovu.",
        ...SPATNE, lzeZrusit: true, lzePozvatZnovu: true,
      };
    }
    return {
      kod: "ceka", label: "Čeká na přijetí",
      popis: dni === null
        ? "Poradce ji musí potvrdit v Nastavení. Datum platnosti neznáme."
        : `Poradce ji musí potvrdit v Nastavení. Zbývá ${dni} ${dni === 1 ? "den" : dni < 5 ? "dny" : "dní"}.`,
      ...CEKA, lzeZrusit: true, lzePozvatZnovu: false,
    };
  }
  /* Nový backend (14. 10. 2026) při „Pozvat znovu“ přepne prošlou pending na
     expired. Je to tatáž vypršelá pozvánka — jen ji už nejde zrušit. */
  if (status === "expired") {
    return {
      kod: "vyprsela", label: "Vypršela",
      popis: "Poradce ji nestihl přijmout. Pozvi ho znovu.",
      ...SPATNE, lzeZrusit: false, lzePozvatZnovu: true,
    };
  }
  if (status === "accepting") {
    return {
      kod: "probiha", label: "Přijímá se…",
      popis: "Poradce právě pozvánku potvrzuje.",
      ...CEKA, lzeZrusit: false, lzePozvatZnovu: false,
    };
  }
  if (status === "accepted") {
    return {
      kod: "prijato", label: "Přijato",
      popis: "Poradce je ve tvém týmu.",
      ...DOBRE, lzeZrusit: false, lzePozvatZnovu: false,
    };
  }
  if (status === "rejected") {
    return {
      kod: "odmitnuto", label: "Odmítnuto",
      popis: "Poradce pozvánku odmítl.",
      ...NEUTRAL, lzeZrusit: false, lzePozvatZnovu: true,
    };
  }
  if (status === "revoked") {
    return {
      kod: "zruseno", label: "Zrušeno",
      popis: "Pozvánku jsi zrušil ty.",
      ...NEUTRAL, lzeZrusit: false, lzePozvatZnovu: true,
    };
  }
  if (status === "conflict") {
    return {
      kod: "konflikt", label: "Nelze přijmout",
      popis: "Poradce má potvrzeného jiného manažera. Musí ho nejdřív opustit.",
      ...SPATNE, lzeZrusit: false, lzePozvatZnovu: false,
    };
  }
  return {
    kod: "neznamy", label: "Neznámý stav",
    popis: "Tenhle stav aplikace nezná — nedomýšlíme si ho.",
    ...NEUTRAL, lzeZrusit: false, lzePozvatZnovu: false,
  };
}

/** Řadí: nejdřív to, co vyžaduje pozornost (čeká, vypršela), pak zbytek. */
const PORADI = { ceka: 0, vyprsela: 1, probiha: 2, konflikt: 3, prijato: 4, odmitnuto: 5, zruseno: 6, neznamy: 7 };

export function serazenePozvanky(seznam, ted = new Date()) {
  const pole = Array.isArray(seznam) ? seznam.filter(Boolean) : [];
  return pole
    .map((inv) => ({ inv, stav: stavPozvanky(inv, ted) }))
    .sort((a, b) => {
      const p = (PORADI[a.stav.kod] ?? 9) - (PORADI[b.stav.kod] ?? 9);
      if (p !== 0) return p;
      const da = parseDatum(a.inv.created_at);
      const dbb = parseDatum(b.inv.created_at);
      return (dbb ? dbb.getTime() : 0) - (da ? da.getTime() : 0);
    });
}

/* Verze věty „co manažer vidí“ (vykonTymu.informace.prijeti, 12. 10. 2026).
 * Posílá se v těle přijetí pozvánky; backend ji zapíše na vztah JEN tehdy,
 * když ji klient poslal (routes/manager.py INFORMACE_VERZE). Změní-li se věta,
 * musí se změnit verze tady i na serveru. */
export const INFORMACE_VERZE = "vykon-tymu-2026-10";

/* Přijetí by vytvořilo kruh v týmu (zvoucí je pod zvaným). Pozvánka přitom
 * dál platí — „Pozvánka už neplatí“ by poradci řekla špatný problém i špatnou
 * nápravu. Překlad do 5 jazyků přes sdílený slovník (data/prekladyUI.data). */
const KRUH_V_TYMU = "Ten, kdo tě zve, je ve tvém týmu pod tebou. Pozvánku nejde přijmout — odmítni ji, nebo ať tě pozve někdo jiný.";

const CHYBY_CS = {
  vztahUkoncen: "Tenhle vztah s manažerem už byl ukončen. Přijetím se neobnoví — požádej manažera o novou pozvánku.",
  jinyManazer: "Už máš potvrzeného manažera. Nejdřív odejdi z týmu v Nastavení → Můj tým, pak pozvánku přijmi.",
  neplatna: "Pozvánka už neplatí — buď vypršela (platí 14 dní), nebo ji manažer zrušil. Požádej o novou.",
  neniProTebe: "Tahle pozvánka není pro tvůj účet. Nejspíš dorazila na jiný email — přihlas se pod tím účtem.",
  nenalezena: "Pozvánka už neexistuje — manažer ji nejspíš zrušil.",
  nedostupne: "Server je chvíli nedostupný. Zkus to prosím za okamžik znovu.",
  sit: "Nepodařilo se spojit se serverem. Zkontroluj připojení a zkus to znovu.",
  obecna: "Akce se nepovedla. Zkus to prosím znovu.",
};

const CHYBY_SK = {
  vztahUkoncen: "Tento vzťah s manažérom už bol ukončený. Prijatím sa neobnoví — požiadaj manažéra o novú pozvánku.",
  jinyManazer: "Už máš potvrdeného manažéra. Najprv odíď z tímu v Nastaveniach → Môj tím, potom pozvánku prijmi.",
  neplatna: "Pozvánka už neplatí — buď vypršala (platí 14 dní), alebo ju manažér zrušil. Požiadaj o novú.",
  neniProTebe: "Táto pozvánka nie je pre tvoj účet. Zrejme prišla na iný email — prihlás sa pod tým účtom.",
  nenalezena: "Pozvánka už neexistuje — manažér ju zrejme zrušil.",
  nedostupne: "Server je chvíľu nedostupný. Skús to prosím o chvíľu znova.",
  sit: "Nepodarilo sa spojiť so serverom. Skontroluj pripojenie a skús to znova.",
  obecna: "Akcia sa nepodarila. Skús to prosím znova.",
};

/**
 * Chyba z accept/reject/cancel → věta, které poradce rozumí.
 * Backend posílá přesné české detaily, ale psané z pohledu manažera a bez rady
 * co dělat dál — proto se překládají, ne jen prohazují na obrazovku.
 */
export function chybaPozvanky(err, lang = "cs") {
  const T = lang === "sk" ? CHYBY_SK : CHYBY_CS;
  const res = err && err.response;
  if (!res) return T.sit;
  const detail = String((res.data && res.data.detail) || "");
  if (res.status === 403) return T.neniProTebe;
  if (res.status === 404) return T.nenalezena;
  if (res.status === 409) {
    if (detail.includes("pod tebou v týmu")) return TLj(KRUH_V_TYMU, lang);
    if (detail.includes("Vztah byl ukončen")) return T.vztahUkoncen;
    if (detail.includes("potvrzeného manažera")) return T.jinyManazer;
    return T.neplatna;
  }
  if (res.status === 503) return T.nedostupne;
  return detail || T.obecna;
}

const NACTENI_CS = {
  neznamaRouta: "Server tuhle funkci zatím neumí — je potřeba nasadit novou verzi backendu.",
  bezPristupu: "K téhle části nemáš přístup.",
  obecna: "Data se nepodařilo načíst.",
};

const NACTENI_SK = {
  neznamaRouta: "Server túto funkciu zatiaľ nevie — je potrebné nasadiť novú verziu backendu.",
  bezPristupu: "K tejto časti nemáš prístup.",
  obecna: "Dáta sa nepodarilo načítať.",
};

/**
 * Chyba při ČTENÍ stavu (GET). Odděleno od chybaPozvanky schválně: 404 na
 * seznamu neznamená „pozvánka byla zrušena“, ale že routa na serveru neběží
 * (typicky backend bez restartu po nasazení). Kdyby se to spletlo, poradce by
 * hledal zrušenou pozvánku místo aby řekl adminovi, ať restartuje server.
 */
export function chybaNacteni(err, lang = "cs") {
  const T = lang === "sk" ? CHYBY_SK : CHYBY_CS;
  const N = lang === "sk" ? NACTENI_SK : NACTENI_CS;
  const res = err && err.response;
  if (!res) return T.sit;
  if (res.status === 404 || res.status === 405) return N.neznamaRouta;
  if (res.status === 401 || res.status === 403) return N.bezPristupu;
  if (res.status === 503) return T.nedostupne;
  return String((res.data && res.data.detail) || "") || N.obecna;
}

/** Jméno zvoucího / manažera — nikdy si ho nevymýšlíme. */
export function jmenoOsoby(name, email, lang = "cs") {
  if (name && String(name).trim()) return String(name).trim();
  if (email && String(email).trim()) return String(email).trim();
  return TLj("Účet se nepodařilo načíst", lang);
}
